// src/modules/app-views/collaborators/pages/Document/tabs/ProfessionalTab/SystemUserLinkCard.jsx
// *******************************************************************************
// SYSTEM USER LINK CARD
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************

// 1. React and hooks
import { useCallback, useMemo, useRef, useState } from 'react';

// 2. External libraries
import { Alert, Button, Card, Flex, Select, Tag } from 'antd';
import { DisconnectOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';

// 3. Providers / Context
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

// 4. Components
import RoundedAvatar from 'layouts/SharedComponents/RoundedAvatar';

// 5. Services and stores
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************

const SystemUserLinkCard = () => {
  // ---------------------------------------------------------------------------
  // PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------

  const { data, viewMode } = useDocumentContext();
  const { setAdditionalFields } = useSimpleFormContext() || {};
  const storedTeam = useZustand('auth', 'team');

  // ---------------------------------------------------------------------------
  // HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES I
  // ---------------------------------------------------------------------------

  const isLinkedOnServer = !!data?.accountId;
  const team = storedTeam || [];
  const isLocked = viewMode === 'view';

  // Find current linked member in team
  const linkedMemberFromServer = useMemo(() => {
    if (!isLinkedOnServer) return null;
    return team.find(m => m.accountId?.toString() === data.accountId?.toString());
  }, [isLinkedOnServer, team, data?.accountId]);

  // Build select options from team members (only active unlinked members)
  const teamOptions = useMemo(() => {
    return team
      .filter(m => !m.entityId && m.status === 2)
      .map(m => ({
        value: m.accountId,
        label: m.name || m.email,
        member: m,
      }));
  }, [team]);

  // Auto-suggest: find team member whose email or phone matches entity contacts
  const suggestedAccountId = useMemo(() => {
    if (isLinkedOnServer || !data?.contacts?.length) return null;

    const entityEmails = (data.contacts || [])
      .filter(c => c.contactType === 'email')
      .map(c => c.value?.toLowerCase());

    const entityPhones = (data.contacts || [])
      .filter(c => c.contactType === 'phone')
      .map(c => c.value);

    for (const m of team) {
      if (m.entityId) continue;

      const memberEmail = (
        typeof m.email === 'object' ? m.email?.value : m.email
      )?.toLowerCase();
      const memberPhone = typeof m.phone === 'object' ? m.phone?.value : m.phone;

      if (memberEmail && entityEmails.includes(memberEmail)) return m.accountId;
      if (memberPhone && entityPhones.includes(memberPhone)) return m.accountId;
    }

    return null;
  }, [isLinkedOnServer, data?.contacts, team]);

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------

  const [selectedAccountId, setSelectedAccountId] = useState(suggestedAccountId);
  const [open, setOpen] = useState(false);

  // Pending action: null = no change, 'link' = pending link, 'unlink' = pending unlink
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingAccountId, setPendingAccountId] = useState(null);

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------

  // Reset pending state when data object reference changes (Refresh, Save, navigation)
  // The data reference changes on EVERY server fetch, making this bulletproof
  const prevDataRef = useRef(data);
  if (prevDataRef.current !== data) {
    prevDataRef.current = data;

    // Reset local pending state synchronously during render
    if (pendingAction !== null) {
      setPendingAction(null);
      setPendingAccountId(null);
      setSelectedAccountId(null);
    }

    // Clear additionalFields (deferred — React handles the re-render)
    setAdditionalFields?.(prev => {
      if (!prev?.['__accountLink.action']) return prev;
      return {
        ...prev,
        '__accountLink.action': undefined,
        '__accountLink.accountId': undefined,
        '__accountLink.avatarSrc': undefined,
      };
    });
  }

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  const handleLink = useCallback(() => {
    if (!selectedAccountId || !data?._id) return;

    // Find the member's avatar to show immediately in CardHeaderAvatar
    const member = team.find(m => m.accountId?.toString() === selectedAccountId?.toString());
    const memberAvatarSrc = member?.avatar?.base64Thumb || member?.avatar?.thumbUrl || null;

    // Store pending action in local state (will be executed on Save)
    setPendingAction('link');
    setPendingAccountId(selectedAccountId);

    // Pass pending link info + avatar to additionalFields
    setAdditionalFields?.(prev => ({
      ...prev,
      '__accountLink.action': 'link',
      '__accountLink.accountId': selectedAccountId,
      '__accountLink.avatarSrc': memberAvatarSrc,
    }));
  }, [selectedAccountId, data?._id, team, setAdditionalFields]);

  const handleUnlink = useCallback(() => {
    if (!data?.accountId || !data?._id) return;

    // Store pending action in local state (will be executed on Save)
    setPendingAction('unlink');
    setPendingAccountId(data.accountId);

    // Pass pending unlink info and clear avatar
    setAdditionalFields?.(prev => ({
      ...prev,
      '__accountLink.action': 'unlink',
      '__accountLink.accountId': data.accountId,
      '__accountLink.avatarSrc': null,
    }));
  }, [data?.accountId, data?._id, setAdditionalFields]);

  const handleCancelPending = useCallback(() => {
    setPendingAction(null);
    setPendingAccountId(null);
    setSelectedAccountId(suggestedAccountId);

    // Clear all pending state from additionalFields
    setAdditionalFields?.(prev => ({
      ...prev,
      '__accountLink.action': undefined,
      '__accountLink.accountId': undefined,
      '__accountLink.avatarSrc': undefined,
    }));
  }, [suggestedAccountId, setAdditionalFields]);

  // ANTD v6 fix: controlled open state ensures dropdown closes on first click
  const handleSelect = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpenChange = useCallback(visible => {
    setOpen(visible);
  }, []);

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // Determine effective state (server state + pending action)
  const effectivelyLinked = pendingAction === 'link' || (isLinkedOnServer && pendingAction !== 'unlink');

  // Find the member to display (from pending link or server state)
  const displayMember = useMemo(() => {
    if (pendingAction === 'link') {
      return team.find(m => m.accountId?.toString() === pendingAccountId?.toString());
    }
    if (pendingAction === 'unlink') return null;
    return linkedMemberFromServer;
  }, [pendingAction, pendingAccountId, linkedMemberFromServer, team]);

  const hasPendingChange = pendingAction !== null;

  // ---------------------------------------------------------------------------
  // UI — linked state: show linked user info with unlink button
  // ---------------------------------------------------------------------------

  if (effectivelyLinked && displayMember) {
    return (
      <Card title={translateX('system_user_link')} className="mb-3">
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={12}>
            <RoundedAvatar record={displayMember} showName showEmail hideLoading />
            {hasPendingChange ? (
              <Tag color="orange">{translateX('pending_save')}</Tag>
            ) : (
              <Tag color="green">{translateX('linked_user')}</Tag>
            )}
          </Flex>

          <Flex gap={8}>
            {hasPendingChange && (
              <Button onClick={handleCancelPending}>
                {translateX('cancel')}
              </Button>
            )}
            {!isLocked && !hasPendingChange && (
              <Button
                icon={<DisconnectOutlined />}
                danger
                onClick={handleUnlink}
              >
                {translateX('unlink_system_user')}
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // UI — not linked: show select to choose a team member
  // ---------------------------------------------------------------------------

  return (
    <Card title={translateX('system_user_link')} className="mb-3">
      {hasPendingChange && pendingAction === 'unlink' && (
        <Alert
          type="warning"
          title={translateX('pending_unlink_save')}
          showIcon
          className="mb-3"
        />
      )}

      {!hasPendingChange && suggestedAccountId && (
        <Alert
          type="info"
          title={translateX('link_system_user')}
          showIcon
          className="mb-3"
        />
      )}

      <Flex gap={12} align="center">
        <UserOutlined style={{ fontSize: 18, color: '#999' }} />
        <Select
          style={{ flex: 1 }}
          placeholder={translateX('link_system_user')}
          options={teamOptions}
          value={selectedAccountId}
          onChange={setSelectedAccountId}
          showSearch
          optionFilterProp="label"
          allowClear
          disabled={isLocked || hasPendingChange}
          open={open}
          onSelect={handleSelect}
          onOpenChange={handleOpenChange}
        />
        {hasPendingChange ? (
          <Button onClick={handleCancelPending}>
            {translateX('cancel')}
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<LinkOutlined />}
            disabled={!selectedAccountId || isLocked}
            onClick={handleLink}
          >
            {translateX('link_system_user')}
          </Button>
        )}
      </Flex>

      {!isLinkedOnServer && !hasPendingChange && !suggestedAccountId && (
        <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
          {translateX('not_linked')}
        </div>
      )}
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************

export default SystemUserLinkCard;
